import { useId, useMemo, useState, type Ref } from "react";
import type { Recording } from "../api/parse.ts";
import { closeDialog, useDialog } from "./admin.tsx";
import { ColumnFilter, FilterChips, type FilterChip, type FilterChoice } from "./columnFilter.tsx";
import { DataTable, type DataColumn } from "./dataTable.tsx";
import { useDialogFocusTrap } from "./dialogFocus.ts";
import { IconAction } from "./iconAction.tsx";
import { recordingDetailFields } from "./recordingDetailFields.ts";
import {
  DEFAULT_RECORDING_SORT,
  NO_RECORDING_FILTERS,
  RECORDING_COLUMN_LABELS,
  RECORDING_FILTER_COLUMNS,
  createRecordingFacetMemo,
  hasRecordingFilters,
  recordingFilterDisplayLabel,
  recordingWhenCell,
  selectedRecordingFilterCount,
  sortRecordingRows,
  toggleRecordingFilter,
  type RecordingFilterColumn,
  type RecordingFilters,
  type RecordingSort,
} from "./recordingTableState.ts";
import { RECORDINGS_TABLE_ID } from "./tableState.ts";
import { formatDuration, formatFileSize } from "./sessionWhen.ts";

const TRANSCRIPT = "text/x-powershell-transcript";

function searchFiltered(items: Recording[] | null, query: string, from: string, to: string): Recording[] {
  if (items === null) return [];
  const needle = query.trim().toLowerCase();
  const fromMs = from.length === 0 ? null : Date.parse(`${from}T00:00:00`);
  const toMs = to.length === 0 ? null : Date.parse(`${to}T23:59:59`);
  return items.filter((recording) => {
    const started = Date.parse(recording.startedAt);
    if (fromMs !== null && Number.isFinite(fromMs) && Number.isFinite(started) && started < fromMs) return false;
    if (toMs !== null && Number.isFinite(toMs) && Number.isFinite(started) && started > toMs) return false;
    if (needle.length === 0) return true;
    const haystack = `${recording.deviceName} ${recording.connectedUser} ${recording.recordingId} ${recording.deviceId} ${recording.sessionId}`.toLowerCase();
    return haystack.includes(needle);
  });
}

function RecordingDetailsDialog(props: { recording: Recording; timeZone?: string; onClose: () => void }) {
  const dialog = useDialog();
  const titleId = useId();
  useDialogFocusTrap(dialog, props.onClose);
  const fields = recordingDetailFields(props.recording, props.timeZone);
  return (
    <dialog
      ref={dialog}
      className="grant-dialog"
      aria-labelledby={titleId}
      onClose={props.onClose}
      onCancel={(event) => {
        event.preventDefault();
        props.onClose();
      }}
    >
      <div className="fields device-details-dialog">
        <h2 id={titleId}>{props.recording.deviceName}</h2>
        <dl className="device-details-list">
          {fields.map((field) => (
            <div key={field.key}>
              <dt>{field.label}</dt>
              <dd>{field.value}</dd>
            </div>
          ))}
        </dl>
        <div className="actions">
          <button type="button" className="secondary" onClick={() => closeDialog(dialog.current)}>
            Close
          </button>
        </div>
      </div>
    </dialog>
  );
}

export function SessionRecordings(props: {
  items: Recording[] | null;
  error: string | null;
  playbackRef: Ref<HTMLVideoElement>;
  playbackUrl: string | null;
  playbackKind?: "video" | "transcript";
  playbackText?: string;
  filterTimeZone?: string;
  onPlay: (recordingId: string) => void;
  onDownload: (recordingId: string) => void;
}) {
  const filterTimeZone = props.filterTimeZone;
  const [query, setQuery] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [filters, setFilters] = useState<RecordingFilters>(NO_RECORDING_FILTERS);
  const [sort, setSort] = useState<RecordingSort>(DEFAULT_RECORDING_SORT);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const facetMemo = useMemo(() => createRecordingFacetMemo(), []);
  const baseRows = useMemo(() => searchFiltered(props.items, query, from, to), [props.items, query, from, to]);
  const facets = facetMemo(baseRows, filters, filterTimeZone);
  const sorted = useMemo(() => sortRecordingRows(facets.rows, sort), [facets.rows, sort]);
  const detailed = detailsId ? sorted.find((item) => item.recordingId === detailsId) ?? null : null;

  function updateFilters(next: RecordingFilters) {
    setFilters(next);
  }

  function toggle(column: RecordingFilterColumn, value: string) {
    updateFilters(toggleRecordingFilter(filters, column, value));
  }

  function onSort(key: string) {
    const column = RECORDING_FILTER_COLUMNS.find((item) => item === key);
    if (!column) return;
    setSort({ column, direction: sort.column === column && sort.direction === "asc" ? "desc" : "asc" });
  }

  function choices(column: RecordingFilterColumn): FilterChoice[] {
    const facet = facets.columns.get(column);
    const selected = filters.values.get(column);
    return (facet?.options ?? []).map((option) => ({
      key: `value:${option.value}`,
      label: option.label,
      count: String(option.count),
      checked: selected?.has(option.value) === true,
      onToggle: () => toggle(column, option.value),
    }));
  }

  const chips: FilterChip[] = [];
  for (const column of RECORDING_FILTER_COLUMNS) {
    for (const value of filters.values.get(column) ?? []) {
      chips.push({
        key: `value:${column}:${value}`,
        column: RECORDING_COLUMN_LABELS[column],
        label: recordingFilterDisplayLabel(column, value),
        onRemove: () => toggle(column, value),
      });
    }
  }

  function filterFor(column: RecordingFilterColumn) {
    return (
      <ColumnFilter
        label={RECORDING_COLUMN_LABELS[column]}
        choices={() => choices(column)}
        selected={selectedRecordingFilterCount(filters, column)}
      />
    );
  }

  const columns: DataColumn<Recording>[] = [
    {
      key: "device",
      label: RECORDING_COLUMN_LABELS.device,
      defaultWidth: 120,
      minWidth: 96,
      sortable: true,
      rowHeader: true,
      filter: filterFor("device"),
      cell: (recording) => (
        <span className="cell-clip" title={recording.deviceName}>
          {recording.deviceName}
        </span>
      ),
    },
    {
      key: "user",
      label: RECORDING_COLUMN_LABELS.user,
      defaultWidth: 120,
      minWidth: 96,
      sortable: true,
      filter: filterFor("user"),
      cell: (recording) => recording.connectedUser,
    },
    {
      key: "start",
      label: RECORDING_COLUMN_LABELS.start,
      defaultWidth: 168,
      minWidth: 120,
      sortable: true,
      filter: filterFor("start"),
      cell: (recording) => recordingWhenCell(recording.startedAt, filterTimeZone),
    },
    {
      key: "end",
      label: RECORDING_COLUMN_LABELS.end,
      defaultWidth: 168,
      minWidth: 120,
      sortable: true,
      filter: filterFor("end"),
      cell: (recording) => (recording.endedAt ? recordingWhenCell(recording.endedAt, filterTimeZone) : "In progress"),
    },
    {
      key: "duration",
      label: RECORDING_COLUMN_LABELS.duration,
      defaultWidth: 88,
      minWidth: 72,
      sortable: true,
      filter: filterFor("duration"),
      cell: (recording) => formatDuration(recording.startedAt, recording.endedAt),
    },
    {
      key: "size",
      label: RECORDING_COLUMN_LABELS.size,
      defaultWidth: 88,
      minWidth: 72,
      sortable: true,
      filter: filterFor("size"),
      cell: (recording) => formatFileSize(recording.bytes),
    },
    {
      key: "actions",
      label: "Actions",
      defaultWidth: 148,
      minWidth: 148,
      className: "device-actions",
      cell: (recording) => (
        <div className="actions">
          <IconAction label="Details" kind="details" onClick={() => setDetailsId(recording.recordingId)} />
          <IconAction label="Play" kind="play" onClick={() => props.onPlay(recording.recordingId)} />
          <IconAction label="Download" kind="download" onClick={() => props.onDownload(recording.recordingId)} />
        </div>
      ),
    },
  ];

  const emptyCatalogue = props.items !== null && props.items.length === 0;
  const emptyFiltered =
    props.items !== null &&
    props.items.length > 0 &&
    sorted.length === 0 &&
    (needleActive(query, from, to) || hasRecordingFilters(filters));

  return (
    <section className="panel">
      <header className="panel-head">
        <h2>Session Recordings</h2>
      </header>
      <div className="recording-filters">
        <label>
          Search
          <input aria-label="Recording search" value={query} onChange={(event) => setQuery(event.target.value)} />
        </label>
        <label>
          From
          <input aria-label="Recording from" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label>
          To
          <input aria-label="Recording to" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
      </div>
      {props.error ? <p className="banner error">{props.error}</p> : null}
      {props.items === null && props.error === null ? <p>Loading recordings.</p> : null}
      {emptyCatalogue ? <p>No recordings yet.</p> : null}
      {emptyFiltered ? <p>No recordings match these filters.</p> : null}
      <FilterChips chips={chips} onClearAll={() => updateFilters(NO_RECORDING_FILTERS)} />
      {detailed ? (
        <RecordingDetailsDialog recording={detailed} timeZone={filterTimeZone} onClose={() => setDetailsId(null)} />
      ) : null}
      {sorted.length > 0 ? (
        <div className="device-table-wrap">
          <DataTable
            className="device-table recording-table"
            rows={sorted}
            rowKey={(recording) => recording.recordingId}
            columns={columns}
            sort={{ key: sort.column, direction: sort.direction }}
            onSort={onSort}
            tableId={RECORDINGS_TABLE_ID}
            rowClassName={(recording) =>
              (recording.contentType ?? "").startsWith(TRANSCRIPT) ? "recording-stealth" : undefined
            }
          />
        </div>
      ) : null}
      {props.playbackUrl && props.playbackKind === "transcript" ? (
        <pre className="ps-console">{props.playbackText}</pre>
      ) : props.playbackUrl ? (
        <video ref={props.playbackRef} className="playback" src={props.playbackUrl} controls playsInline />
      ) : null}
    </section>
  );
}

function needleActive(query: string, from: string, to: string): boolean {
  return query.trim().length > 0 || from.length > 0 || to.length > 0;
}
